import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-button>` says about itself.
 *
 * The examples here are the reason this file exists rather than a nicety. The library page used to
 * show ONE example of the button holding three elements — a primary, a secondary, and a third
 * labelled "Saving" drawn with `disabled`. The button has a `loading` attribute, it is observed, and
 * it draws a spinner; the example did not use it. So the one page a person visits to learn what the
 * component does taught them to express BUSY by DISABLING, which is a different statement:
 * `disabled` means you may not do this, `loading` means this is happening. One of them should be set
 * by a network call finishing and the other never should.
 *
 * That happened because there was one example slot and two things to say, so the two shared it and
 * one of them lost. They are two named examples now, side by side, which is what having more than
 * one example is FOR.
 *
 * CAOS-599 added the icon mode and the pressed state, and the examples matter for the same reason
 * again: a catalogue that ships four button components teaches four components. This page is where
 * a person finds out that the icon-only button and the toggle they were looking for are this one.
 */
const docs: ComponentDocs = {
  // `screen` is here for CAOS-598, and it is the only piece that gained it with the wizard surface.
  // A guided flow has to be able to REACH its next step, and the button is the only thing in the
  // library that moves anything; a wizard page that cannot carry one is a flow nobody can finish.
  // Every other piece keeps the placements it already declared — see `placementForPageKind` in the
  // kernel for why that restraint is deliberate rather than an oversight.
  placements: ['recordPage', 'homePage', 'appPage', 'recordAction', 'screen', 'seam'],

  inputs: [
    {
      name: 'variant',
      attribute: 'variant',
      type: 'text',
      options: ['primary', 'secondary', 'soft', 'ghost', 'destructive'],
      description:
        'Which of the five colour treatments to draw: primary, secondary, soft, ghost or destructive.',
      default: 'secondary',
    },
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: ['sm', 'md', 'lg'],
      description:
        'Control height from the platform size scale — sm, md or lg. Inside a button group the ' +
        'group’s size wins, so a joined run cannot be assembled out of buttons at three heights.',
      default: 'md',
    },
    {
      name: 'icon',
      attribute: 'icon',
      type: 'text',
      description:
        'A glyph from the platform’s one vocabulary, drawn beside the label — or on its own, if ' +
        'nothing is slotted in as a label. A name the vocabulary does not carry draws nothing rather ' +
        'than an empty box.',
    },
    {
      name: 'iconPosition',
      attribute: 'icon-position',
      type: 'text',
      options: ['start', 'end'],
      description: 'Which side of the label the glyph sits on.',
      default: 'start',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What this button is, for a screen reader. Required when the button has a glyph and no ' +
        'slotted label, because there is then no text to read; otherwise the slotted label is the ' +
        'name and this is only for saying something different to it.',
    },
    {
      name: 'pressed',
      attribute: 'pressed',
      type: 'text',
      options: ['true', 'false'],
      description:
        'A toggle, and which way it is set. Leaving it off means this is not a toggle and the ' +
        'control says nothing about being pressed; false is as load-bearing as true, because ' +
        'without it a toggle that is off cannot be told from a button that never toggles.',
    },
    {
      name: 'loading',
      attribute: 'loading',
      type: 'boolean',
      description:
        'This action is happening. Draws a spinner, marks the control busy for a screen reader, and ' +
        'stops it being pressed again. A progress statement, never a permission one.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description:
        'You may not do this. A permission statement — it should not be set because something is in ' +
        'flight; that is what loading is for.',
      default: false,
    },
    {
      name: 'block',
      attribute: 'block',
      type: 'boolean',
      description:
        'Fill the width of whatever holds it, instead of hugging its label. An icon-only button ' +
        'set this way fills the width and keeps its height, rather than growing into a square.',
      default: false,
    },
    {
      name: 'type',
      attribute: 'type',
      type: 'text',
      options: ['button', 'submit', 'reset'],
      description:
        'button, submit or reset. A submit or reset button forwards the action to the form around it, ' +
        'which a plain shadow button cannot do on its own.',
      default: 'button',
    },
  ],

  // Declared as NONE rather than left unsaid: a button raises the ordinary DOM click and nothing of
  // its own, and saying so is the useful answer. Silence would read as "nobody has written this
  // down", which is a different and less helpful fact.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The label. Leave it empty and give the button an icon to get the icon-only mode — that ' +
        'is the whole of what “icon button” means here.',
    },
  ],

  tokens: [
    { token: '--caos-radius', controls: 'The corner radius of the control.' },
    { token: '--caos-button-radius-start', controls: 'The leading corners. A button group sets it to 0 to square the corners it shares with the button before it; nothing else should.' },
    { token: '--caos-button-radius-end', controls: 'The trailing corners, set the same way for the button after it. Start and end rather than left and right, so a joined run reads correctly in a right-to-left script.' },
    { token: '--caos-state-selected-bg', controls: 'The fill of a pressed toggle — the same one a selected row carries, so “in force” reads the same everywhere.' },
    { token: '--caos-color-accent-fill', controls: 'The fill of the primary variant, and --caos-color-accent-fill-hover under the pointer.' },
    { token: '--caos-color-accent-contrast', controls: 'The label and spinner of the primary variant. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-color-danger-fill', controls: 'The fill of the destructive variant under the pointer.' },
    { token: '--caos-color-danger-contrast', controls: 'The label and spinner of the destructive variant under the pointer.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn when the control has keyboard focus.' },
    { token: '--caos-space-2', controls: 'The gap between a glyph and the label.' },
    { token: '--caos-font-display', controls: 'The typeface the label is set in.' },
  ],

  parts: [
    { part: 'button', is: 'the control itself' },
    { part: 'spinner', is: 'the busy indicator drawn while loading' },
    { part: 'icon', is: 'the glyph, when the button has one' },
  ],

  methods: [],

  placing:
    'A button is placed like any other piece: as an item in a page section. It carries no record ' +
    'binding of its own, so what it DOES comes from the action declared on the placement rather than ' +
    'from the button.',

  whenToUse:
    'One action, pressed directly. Reach for it whenever a person is meant to do a single thing from ' +
    'where they are standing.',

  insteadUse: [
    { name: 'ActionBar', when: 'A surface has several actions and one of them is the primary one.' },
    { name: 'OverflowMenu', when: 'The actions are secondary and belong behind a single trigger.' },
    { name: 'SegmentedFilter', when: 'The row of controls changes what is shown rather than doing something.' },
  ],

  accessibility:
    'The control is a real button, so keyboard activation, focus order and the focus ring are already ' +
    'handled. When loading is set it is marked busy for a screen reader and cannot be pressed again. ' +
    'A toggle announces itself as one: pressed maps straight onto aria-pressed, and setting it to ' +
    'false is what tells a screen reader this is a toggle that is currently off rather than an ' +
    'ordinary button. What is left to the author is the LABEL: an icon-only button has no text to ' +
    'read, so its label attribute is the only name it has, and a label reading "Submit" on a page ' +
    'with three of them needs to say which one.',

  examples: [
    {
      key: 'variants',
      label: 'The five variants',
      summary: 'Every colour treatment the button has, so the one that fits can be picked by looking.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'primary' }, children: ['Save'] },
        { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Cancel'] },
        { component: 'caos-button', inputs: { variant: 'soft' }, children: ['Duplicate'] },
        { component: 'caos-button', inputs: { variant: 'ghost' }, children: ['Dismiss'] },
        { component: 'caos-button', inputs: { variant: 'destructive' }, children: ['Delete record'] },
      ],
    },
    {
      key: 'busy',
      label: 'Busy',
      summary:
        'A save that is in flight: the spinner draws, the control is marked busy for a screen reader, ' +
        'and it cannot be pressed twice.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'primary', loading: true }, children: ['Saving'] },
        { component: 'caos-button', inputs: { variant: 'secondary', loading: true }, children: ['Checking'] },
      ],
    },
    {
      key: 'disabled',
      label: 'Not allowed',
      summary:
        'The same two controls refused rather than busy. They look similar and mean different things, ' +
        'which is exactly why they are two examples and not one.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'primary', disabled: true }, children: ['Save'] },
        { component: 'caos-button', inputs: { variant: 'secondary', disabled: true }, children: ['Cancel'] },
      ],
    },
    {
      key: 'sizes',
      label: 'The size scale',
      summary: 'The three control heights, taken from the platform scale rather than from the button.',
      placement: [
        { component: 'caos-button', inputs: { size: 'sm', variant: 'secondary' }, children: ['Small'] },
        { component: 'caos-button', inputs: { size: 'md', variant: 'secondary' }, children: ['Medium'] },
        { component: 'caos-button', inputs: { size: 'lg', variant: 'secondary' }, children: ['Large'] },
      ],
    },
    {
      key: 'icons',
      label: 'With a glyph',
      summary:
        'A glyph beside the label, and on the other side of it. The glyph comes from the platform’s ' +
        'one vocabulary — there is no second set to pick from.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'primary', icon: 'plus' }, children: ['New record'] },
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'download' }, children: ['Export'] },
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'chevron-right', iconPosition: 'end' }, children: ['Next'] },
      ],
    },
    {
      key: 'icon-only',
      label: 'A glyph on its own',
      summary:
        'The same button with nothing slotted in as a label. This is what an “icon button” is here — ' +
        'a mode, not a second component — and its label attribute is the only name a screen reader ' +
        'has for it.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'square-pen', label: 'Edit' } },
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'share', label: 'Share' } },
        { component: 'caos-button', inputs: { variant: 'destructive', icon: 'close', label: 'Delete' } },
      ],
    },
    {
      key: 'pressed',
      label: 'A toggle, on and off',
      summary:
        'Pressed is a state on this button, beside the busy one — not a stateful button component. ' +
        'The one on the right is a toggle that is OFF, which is a different thing from a button that ' +
        'does not toggle, and only setting pressed to false says so.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'pin', label: 'Pinned', pressed: 'true' } },
        { component: 'caos-button', inputs: { variant: 'secondary', icon: 'pin', label: 'Pin this', pressed: 'false' } },
      ],
    },
    {
      key: 'block',
      label: 'Full width',
      summary: 'Filling whatever holds it — how a button reads at the foot of a narrow panel or a form.',
      placement: { component: 'caos-button', inputs: { variant: 'primary', block: true }, children: ['Submit request'] },
    },
  ],
};

export default docs;
